import React from "react";
import { ArrowRight, Sparkles, Star } from "lucide-react";

export default function Hero() {
    const scrollTo = (id) => (e) => {
        e.preventDefault();
        document.querySelector(id)?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative overflow-hidden"
        >
            {/* Hero-only accent dot grid layer (denser, for focus) */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 dot-grid opacity-[0.25]"
                style={{
                    maskImage:
                        "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 70%)",
                    WebkitMaskImage:
                        "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 70%)",
                }}
            />

            <div className="section-container relative pt-28 sm:pt-36 lg:pt-44 pb-16 sm:pb-24 lg:pb-32">
                <div className="mx-auto max-w-[900px] text-center">
                    {/* Eyebrow pill */}
                    <div
                        className="reveal inline-flex items-center gap-2 rounded-full border border-[rgba(10,10,10,0.1)] bg-white/70 backdrop-blur px-3 py-1 text-[11.5px] sm:text-[12px] text-[#0a0a0a]/70 shadow-[0_1px_0_0_rgba(255,255,255,0.6)_inset,0_8px_24px_-16px_rgba(10,10,10,0.18)]"
                        data-testid="hero-eyebrow"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-[#facc15] opacity-75 animate-ping" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#facc15]" />
                        </span>
                        <span className="font-medium">
                            Now booking — ships in days
                        </span>
                        <span className="text-[#0a0a0a]/25">•</span>
                        <span>For service-based businesses</span>
                    </div>

                    <h1
                        className="reveal font-sans-display mt-5 sm:mt-7 text-[40px] leading-[1.04] sm:text-[64px] sm:leading-[1.02] lg:text-[96px] font-extrabold tracking-[-0.04em] text-[#0a0a0a]"
                        data-testid="hero-headline"
                    >
                        We build <span className="hl-marker">websites</span>
                        <br />
                        that people{" "}
                        <span className="font-display italic font-normal text-[#0a0a0a]">
                            actually
                        </span>{" "}
                        love.
                    </h1>

                    <p
                        className="reveal reveal-delay-1 mt-5 sm:mt-6 max-w-[620px] mx-auto text-[15.5px] sm:text-[18px] leading-[1.55] text-[#0a0a0a]/65 px-1"
                        data-testid="hero-subheadline"
                    >
                        Creston is a small studio that designs, builds, and
                        ships clean, fast, beautiful websites for businesses
                        based in the U.S. No bloat, no templates — just a
                        website that makes your business look the way it
                        deserves to.
                    </p>

                    <div
                        className="reveal reveal-delay-2 mt-8 sm:mt-10 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3 px-2 sm:px-0"
                        data-testid="hero-ctas"
                    >
                        <a
                            href="#contact"
                            onClick={scrollTo("#contact")}
                            className="group inline-flex items-center justify-center h-12 px-6 rounded-full bg-[#0a0a0a] text-[#fbf9f4] text-[15px] font-semibold btn-hard w-full sm:w-auto"
                            data-testid="hero-primary-cta-button"
                        >
                            Get in touch
                            <ArrowRight className="ml-2 size-4 btn-arrow" />
                        </a>
                        <a
                            href="#what"
                            onClick={scrollTo("#what")}
                            className="group inline-flex items-center justify-center h-12 px-6 rounded-full bg-white text-[#0a0a0a] text-[15px] font-semibold border border-[#0a0a0a] btn-hard-ghost w-full sm:w-auto"
                            data-testid="hero-secondary-cta-button"
                        >
                            What we do
                        </a>
                    </div>

                    {/* Trust row — avatars + rating */}
                    <div
                        className="reveal reveal-delay-3 mt-10 sm:mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5"
                        data-testid="hero-trust"
                    >
                        <div className="flex -space-x-2">
                            {[
                                "#f4c06c",
                                "#e39b7b",
                                "#b5c993",
                                "#8fb5c2",
                                "#cdb5d8",
                            ].map((c, i) => (
                                <span
                                    key={i}
                                    className="inline-flex h-7 w-7 rounded-full border-2 border-[hsl(var(--background))] shadow-[0_1px_0_0_rgba(10,10,10,0.08)]"
                                    style={{ background: c }}
                                    aria-hidden
                                />
                            ))}
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[12.5px] sm:text-[13px] text-[#0a0a0a]/70">
                            <div
                                className="flex items-center gap-0.5 text-[#facc15]"
                                aria-label="5 out of 5 stars"
                            >
                                {[0, 1, 2, 3, 4].map((i) => (
                                    <Star
                                        key={i}
                                        className="size-3.5"
                                        fill="currentColor"
                                        strokeWidth={0}
                                    />
                                ))}
                            </div>
                            <span>
                                <span className="font-medium text-[#0a0a0a]">
                                    Loved by
                                </span>
                                {"\u00A0"}20+ business owners across the U.S.
                            </span>
                        </div>
                    </div>

                    {/* Meta row */}
                    <div
                        className="reveal reveal-delay-4 mt-8 sm:mt-10 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[12.5px] sm:text-[13px] text-[#0a0a0a]/55"
                        data-testid="hero-meta"
                    >
                        <div className="flex items-center gap-2">
                            <Sparkles className="size-3.5 text-[#facc15]" />
                            Ships in days, not months
                        </div>
                        <span className="hidden sm:inline text-[#0a0a0a]/20">
                            •
                        </span>
                        <div className="flex items-center gap-2">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#0a0a0a]/40" />
                            Unique, never templated
                        </div>
                        <span className="hidden sm:inline text-[#0a0a0a]/20">
                            •
                        </span>
                        <div className="flex items-center gap-2">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#0a0a0a]/40" />
                            One flat price
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom fade into next section */}
            <div
                aria-hidden
                className="pointer-events-none absolute bottom-0 inset-x-0 h-24 bg-gradient-to-b from-transparent to-[hsl(var(--background))]"
            />
        </section>
    );
}
